import React from 'react';
import { motion } from 'motion/react';
import { ArrowUpRight, BookImage, CalendarDays, CheckCircle2, MapPin } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

type LiveProject = {
  title: string;
  location: string;
  type: string;
  year: string;
  image: string;
  summary: string;
};

type BrochureSection = {
  title: string;
  description: string;
  images: string[];
};

type ArchiveProject = {
  title: string;
  location: string;
  unitMix: string;
  summary: string;
  coverImage: string;
  coverLabel: string;
  highlights: string[];
  sections: BrochureSection[];
};

const publicUrl = process.env.PUBLIC_URL ?? '';
const brochurePage = (slug: string, page: number) => `${publicUrl}/completed-projects/${slug}/page-${page}.png`;
const currentBrochurePage = (slug: string, page: number) => `${publicUrl}/current-projects/${slug}/page-${page}.png`;

const liveProjects: LiveProject[] = [
  {
    title: 'Ridge Courtyard Villas',
    location: 'Jakkur, Bengaluru',
    type: 'Private villa enclave',
    year: '2026 launch',
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80&w=2070',
    summary: 'Low-rise homes arranged around planted inner courts, long eaves, and sheltered arrival gardens.',
  },
  {
    title: 'Canopy Residences',
    location: 'Whitefield, Bengaluru',
    type: 'Apartment collection',
    year: 'Under design',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=2071',
    summary: 'Large terraces, softened facades, and club spaces designed with boutique-hotel discipline.',
  },
  {
    title: 'Imperial Corner',
    location: 'Hebbal, Bengaluru',
    type: 'Mixed-use urban block',
    year: 'In progress',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2070',
    summary: 'Retail frontage, office floors, and landscape buffers built as one clear urban composition.',
  },
];

const currentProjects: ArchiveProject[] = [
  {
    title: 'Signature Villas',
    location: 'Vishwanathapura / Rajanukunte, Bengaluru',
    unitMix: '4 BHK + Home Theater and 5 BHK + Home Theater + Gym villas',
    summary:
      'Signature Villas is presented as an active low-rise villa community focused on privacy, sunlight, ventilation, and brochure-backed planning for each site cluster across the development.',
    coverImage: currentBrochurePage('signature-villas', 1),
    coverLabel: 'Brochure opening',
    highlights: ['16 brochure pages', 'Ground + 2 upper floors', '26 units', '4 BHK + 5 BHK villa mix', '100% vaastu compliant'],
    sections: [
      {
        title: 'Project Facts & Exterior',
        description:
          'The main exterior brochure page introduces Signature Villas, the welcome note, floor count, unit count, and villa typology for the current project.',
        images: [currentBrochurePage('signature-villas', 2)],
      },
      {
        title: 'Earth-Friendly Features',
        description:
          'Lifestyle page highlighting landscaping with native species, eco-friendly building materials, rainwater harvesting, a secured community, and the brochure positioning around staying closer to daily destinations.',
        images: [currentBrochurePage('signature-villas', 3)],
      },
      {
        title: 'Specifications',
        description:
          'Construction and services specification sheet covering super structure, RCC framed structure, plastering, doors, windows, flooring, toilets, kitchen, water supply, painting, car parking, UPS backup, and electrical provisions.',
        images: [currentBrochurePage('signature-villas', 4)],
      },
      {
        title: 'Layout Plan',
        description:
          'Master layout page showing the Signature Villas site arrangement, road network, park, clubhouse and swimming pool zone, and numbered villa clusters.',
        images: [currentBrochurePage('signature-villas', 5)],
      },
      {
        title: 'Villa Plans 01/1 and 01/2',
        description:
          'Detailed plan sheet for the 5 BHK villa pair, including ground floor, first floor, terrace floor, entry orientation, and total built-up area.',
        images: [currentBrochurePage('signature-villas', 6)],
      },
      {
        title: 'Villa Plans 03/1 to 06/2',
        description:
          'Grouped plan sheets for villa pairs 03, 04, and 06, showing 4 BHK layouts, family areas, terraces, home theater levels, and built-up area schedules.',
        images: [
          currentBrochurePage('signature-villas', 7),
          currentBrochurePage('signature-villas', 8),
          currentBrochurePage('signature-villas', 9),
        ],
      },
      {
        title: 'Villa Plans 09/1 to 13/2',
        description:
          'Plan sequence for villa pairs 09, 12, and 13 with ground, first, and terrace arrangements, home theater spaces, and facing details from the brochure.',
        images: [currentBrochurePage('signature-villas', 10), currentBrochurePage('signature-villas', 11)],
      },
      {
        title: 'Villa Plans 17/1 to 23/2',
        description:
          'Remaining plan sheets covering villa pairs 17 through 23, including ground floor, first floor, terrace levels, open terraces, and garden balcony layouts.',
        images: [
          currentBrochurePage('signature-villas', 12),
          currentBrochurePage('signature-villas', 13),
          currentBrochurePage('signature-villas', 14),
        ],
      },
      {
        title: 'Amenities & Nearby Destinations',
        description:
          'Amenities page covering clubhouse and party hall, gym and indoor games, rainwater harvesting, stilt parking, round-the-clock security, landscape garden, UPS backup, CCTV, and surrounding institutions and landmarks.',
        images: [currentBrochurePage('signature-villas', 15)],
      },
      {
        title: 'Location & Contact',
        description:
          'Final brochure page with the Signature Villas location map, full site address, office address, project contact number, and email details for enquiries.',
        images: [currentBrochurePage('signature-villas', 16)],
      },
    ],
  },
];

const completedProjects: ArchiveProject[] = [
  {
    title: 'Murari Abhaya',
    location: 'Kagadasapura, Bengaluru East',
    unitMix: '2 & 3 BHK luxury apartments',
    summary:
      'The brochure positions Murari Abhaya as a nature-led family apartment community with open space between blocks, generous light, cross ventilation, and calm everyday living.',
    coverImage: brochurePage('murari-abhaya', 1),
    coverLabel: 'Brochure cover',
    highlights: ['8 brochure pages', 'Nature-led positioning', '2 BHK + 3 BHK layouts', 'Brochure address: Abbaiah Reddy Layout'],
    sections: [
      {
        title: 'Project Story',
        description:
          'The introduction page highlights Murari Constructions and describes Murari Abhaya as a community planned for openness, ventilation, sunlight, and relaxed family living.',
        images: [brochurePage('murari-abhaya', 2)],
      },
      {
        title: 'Completed Elevation',
        description: 'Rendered building elevation of the completed apartment block shown in the brochure.',
        images: [brochurePage('murari-abhaya', 3)],
      },
      {
        title: 'Plans & Layouts',
        description:
          'Brochure pages covering unit-level plans and the overall typical floor layout, including 2 BHK, 3 BHK, and the broader floor arrangement.',
        images: [brochurePage('murari-abhaya', 4), brochurePage('murari-abhaya', 5), brochurePage('murari-abhaya', 6)],
      },
      {
        title: 'Specifications & Amenities',
        description:
          'Construction specifications, plumbing and electrical notes, flooring and finishes, and resident amenities such as generator backup, lift, security, rainwater harvesting, and parking.',
        images: [brochurePage('murari-abhaya', 7)],
      },
      {
        title: 'Location & Connectivity',
        description:
          'Brochure location map with office address, site address, nearby educational institutions, hospitals, shopping centres, and the BBMP approval mark.',
        images: [brochurePage('murari-abhaya', 8)],
      },
    ],
  },
  {
    title: 'Murari Ananda',
    location: 'Amruth Nagar / Yelahanka, Bengaluru',
    unitMix: '2 & 3 BHK luxury apartments',
    summary:
      'Murari Ananda presents compact premium apartment layouts with east and north-facing units, brochure-backed plan sheets, project specifications, and a location map focused on north Bengaluru connectivity.',
    coverImage: brochurePage('murari-ananda', 1),
    coverLabel: 'Brochure cover',
    highlights: ['4 brochure pages', '1130 to 1460 sft unit mix', 'East + North facing units', 'North Bengaluru connectivity'],
    sections: [
      {
        title: 'Typical Floor Plate',
        description:
          'Area statement and floor arrangement page showing flat mix, circulation, and the 2 BHK / 3 BHK planning layout across the floor.',
        images: [brochurePage('murari-ananda', 2)],
      },
      {
        title: 'Specifications & Amenities',
        description:
          'Brochure specifications page covering RCC structure, blocks, plastering, doors, windows, flooring, toilets, kitchen, electricals, lift, generator backup, CCTV, and other amenities.',
        images: [brochurePage('murari-ananda', 3)],
      },
      {
        title: 'Location Map',
        description:
          'North Bengaluru location sheet with Yelahanka-side connectivity, office and site addresses, and contact details from the brochure.',
        images: [brochurePage('murari-ananda', 4)],
      },
    ],
  },
  {
    title: 'Murari Nest',
    location: 'Vignan Nagar / Kagadasapura, Bengaluru',
    unitMix: '2 & 3 BHK luxury apartments',
    summary:
      'Murari Nest is documented through a compact brochure sequence showing its exterior view, typical floor plate, technical specifications, amenities, and east Bengaluru location context.',
    coverImage: brochurePage('murari-nest', 1),
    coverLabel: 'Brochure cover',
    highlights: ['4 brochure pages', '1170 to 1520 sft plans', 'Kagadasapura location map', 'Covered car parking + generator backup'],
    sections: [
      {
        title: 'Typical Floor Plan',
        description:
          'Layout sheet with unit mix, corridor and lift core arrangement, and area statement for 2 BHK and 3 BHK units.',
        images: [brochurePage('murari-nest', 2)],
      },
      {
        title: 'Specifications & Amenities',
        description:
          'Brochure page covering structural specification, flooring, kitchen, windows, electrical points, car parking, lift, generator backup, rainwater harvesting, and vastu compliance.',
        images: [brochurePage('murari-nest', 3)],
      },
      {
        title: 'Location & Address',
        description:
          'Kagadasapura / KR Puram corridor location sheet listing office address, site address, and surrounding access routes.',
        images: [brochurePage('murari-nest', 4)],
      },
    ],
  },
  {
    title: 'Suraksha Heights',
    location: 'Sahakara Nagar / Bellary Road, Bengaluru',
    unitMix: '2 & 3 BHK luxury apartments',
    summary:
      'Suraksha Heights is documented through a four-page brochure sequence covering the finished elevation, typical floor plate, construction specification sheet, and location map for Sahakara Nagar.',
    coverImage: brochurePage('suraksha-heights', 1),
    coverLabel: 'Brochure cover',
    highlights: ['4 brochure pages', '1180 + 1400 sft plans', 'North-facing unit mix shown', 'Bellary Road / Sahakara Nagar address'],
    sections: [
      {
        title: 'Typical Floor Plan',
        description:
          'Layout page showing the two-flat floor arrangement, road edge, typical floor planning, and area statement for 2 BHK and 3 BHK homes.',
        images: [brochurePage('suraksha-heights', 2)],
      },
      {
        title: 'Specifications & Amenities',
        description:
          'Brochure technical page covering structure, plastering, doors, windows, flooring, toilets, kitchen, electricals, parking, lift, generator backup, CCTV, intercom, and security provisions.',
        images: [brochurePage('suraksha-heights', 3)],
      },
      {
        title: 'Location Map',
        description:
          'Location map and address sheet for Suraksha Heights with Bellary Road / Sahakara Nagar details, contact information, and nearby landmarks.',
        images: [brochurePage('suraksha-heights', 4)],
      },
    ],
  },
];

function getBrochureGridClass(imageCount: number) {
  if (imageCount <= 1) {
    return 'grid-cols-1';
  }

  if (imageCount === 2) {
    return 'grid-cols-1 md:grid-cols-2';
  }

  return 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3';
}

export default function Projects() {
  const location = useLocation();
  const projectCategory = new URLSearchParams(location.search).get('category');
  const isCurrentView = projectCategory === 'current';
  const isCompletedView = projectCategory === 'completed';
  const archiveCardClass = 'overflow-hidden rounded-[2.4rem] border border-white/10 bg-white/[0.04] text-secondary shadow-[0_30px_80px_rgba(0,0,0,0.42)]';
  const archiveMediaPanelClass = 'border-b border-white/10 bg-white/[0.03] p-6 md:p-8 xl:border-b-0 xl:border-r';
  const archiveBadgeClass =
    'mb-6 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/74';
  const archiveImageFrameClass = 'overflow-hidden rounded-[1.75rem] border border-white/10 bg-black/40 shadow-[0_18px_45px_rgba(0,0,0,0.24)]';
  const archiveMetaRowClass = 'flex flex-wrap items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/74';
  const archiveMetaBadgeClass = 'inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2';
  const archiveTitleClass = 'mt-6 text-balance text-4xl text-secondary md:text-5xl';
  const archiveSubheadClass = 'mt-3 text-sm font-semibold uppercase tracking-[0.26em] text-secondary/45';
  const archiveCopyClass = 'mt-6 max-w-3xl text-base leading-8 text-secondary/68 md:text-lg';
  const archiveHighlightClass =
    'rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-secondary/70';
  const archiveSectionsWrapClass = 'border-t border-white/10 bg-black/25 p-6 md:p-8';
  const archiveSectionCardClass =
    'rounded-[1.9rem] border border-white/10 bg-white/[0.03] p-5 shadow-[0_16px_40px_rgba(0,0,0,0.18)] md:p-6';
  const archiveSectionLabelClass = 'text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/60';
  const archiveSectionTitleClass = 'mt-2 text-2xl text-secondary md:text-3xl';
  const archiveSectionCopyClass = 'mt-3 text-base leading-8 text-secondary/62';
  const archiveSectionImageClass = 'overflow-hidden rounded-[1.5rem] border border-white/10 bg-black/40 shadow-[0_14px_30px_rgba(0,0,0,0.18)]';

  if (isCurrentView) {
    return (
      <section className="relative overflow-hidden bg-primary pb-28 pt-36 text-secondary md:pb-32 md:pt-44">
        <div className="paper-grid absolute inset-0 opacity-[0.08]" />
        <div className="floating-orb absolute right-[-10rem] top-16 h-80 w-80 rounded-full bg-accent/15" />

        <div className="section-shell relative">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(320px,0.7fr)] lg:items-end">
            <div>
              <span className="editorial-kicker text-gold-light before:bg-gold-light">Current projects</span>
              <h1 className="mt-8 max-w-5xl text-balance text-4xl leading-tight text-secondary sm:text-5xl md:text-6xl">
                Current projects organised brochure by brochure, section by section.
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-8 text-secondary/72 md:text-lg">
                The current-projects view now uses the supplied Signature Villas brochure pages directly. Exterior views, layout
                plans, villa plans, specifications, amenities, and location details are grouped in the same structured format as
                the completed-project archive.
              </p>
            </div>

            <div className="rounded-[1.75rem] border border-white/10 bg-white/5 p-6 backdrop-blur">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">Current project summary</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="font-serif text-4xl text-secondary">1</p>
                  <p className="mt-2 text-sm leading-6 text-secondary/62">Active brochure-backed villa community</p>
                </div>
                <div>
                  <p className="font-serif text-4xl text-secondary">16</p>
                  <p className="mt-2 text-sm leading-6 text-secondary/62">Rendered brochure pages placed in sections</p>
                </div>
                <div>
                  <p className="font-serif text-4xl text-secondary">26</p>
                  <p className="mt-2 text-sm leading-6 text-secondary/62">Units noted in the Signature Villas brochure</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14 space-y-14">
            {currentProjects.map((project, projectIndex) => (
              <motion.article
                key={project.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.12 }}
                transition={{ duration: 0.65, delay: projectIndex * 0.04 }}
                className={archiveCardClass}
              >
                <div className="grid gap-0 xl:grid-cols-[minmax(360px,0.9fr)_minmax(0,1.1fr)]">
                  <div className={archiveMediaPanelClass}>
                    <div className={archiveBadgeClass}>
                      <BookImage size={14} />
                      {project.coverLabel}
                    </div>

                    <div className={archiveImageFrameClass}>
                      <img
                        src={project.coverImage}
                        alt={`${project.title} brochure opening`}
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  <div className="p-6 md:p-8">
                    <div className={archiveMetaRowClass}>
                      <span className={archiveMetaBadgeClass}>
                        <CalendarDays size={14} />
                        Current Project
                      </span>
                      <span className={archiveMetaBadgeClass}>
                        <MapPin size={14} />
                        {project.location}
                      </span>
                    </div>

                    <h2 className={archiveTitleClass}>{project.title}</h2>
                    <p className={archiveSubheadClass}>{project.unitMix}</p>
                    <p className={archiveCopyClass}>{project.summary}</p>

                    <div className="mt-8 flex flex-wrap gap-3">
                      {project.highlights.map((item) => (
                        <span key={item} className={archiveHighlightClass}>
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={archiveSectionsWrapClass}>
                  <div className="space-y-8">
                    {project.sections.map((section) => (
                      <section key={`${project.title}-${section.title}`} className={archiveSectionCardClass}>
                        <div className="md:flex md:items-start md:justify-between md:gap-8">
                          <div className="max-w-3xl">
                            <p className={archiveSectionLabelClass}>{project.title}</p>
                            <h3 className={archiveSectionTitleClass}>{section.title}</h3>
                            <p className={archiveSectionCopyClass}>{section.description}</p>
                          </div>
                        </div>

                        <div className={`mt-6 grid gap-4 ${getBrochureGridClass(section.images.length)}`}>
                          {section.images.map((image, imageIndex) => (
                            <div key={`${section.title}-${imageIndex}`} className={archiveSectionImageClass}>
                              <img
                                src={image}
                                alt={`${project.title} ${section.title} brochure page ${imageIndex + 1}`}
                                className="h-full w-full object-contain"
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-10 rounded-[2rem] border border-white/10 bg-white/5 p-8 text-center backdrop-blur md:p-10">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">Need the delivered archive?</p>
            <p className="mx-auto mt-4 max-w-2xl text-2xl font-serif text-secondary md:text-3xl">
              Open Project Completed from the Projects submenu to browse brochure sections for finished apartment communities.
            </p>
            <Link
              to="/projects?category=completed"
              className="mt-7 inline-flex items-center gap-3 rounded-full bg-accent px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:bg-gold-light hover:text-primary"
            >
              Open Completed Projects
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (isCompletedView) {
    return (
      <section className="relative overflow-hidden bg-primary pb-28 pt-36 text-secondary md:pb-32 md:pt-44">
        <div className="paper-grid absolute inset-0 opacity-[0.08]" />
        <div className="floating-orb absolute right-[-10rem] top-16 h-80 w-80 rounded-full bg-accent/15" />

        <div className="section-shell relative">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(320px,0.7fr)] lg:items-end">
            <div>
              <span className="editorial-kicker text-gold-light before:bg-gold-light">Project completed</span>
              <h1 className="mt-8 max-w-5xl text-balance text-4xl leading-tight text-secondary sm:text-5xl md:text-6xl">
                Completed projects organised brochure by brochure, section by section.
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-8 text-secondary/72 md:text-lg">
                Each finished project below uses rendered brochure pages from your supplied PDFs. Plans, specifications, amenities,
                and location maps are grouped per project so the completed-projects view reads like a proper archive instead of a mixed gallery.
              </p>
            </div>

            <div className="rounded-[1.75rem] border border-white/10 bg-white/5 p-6 backdrop-blur">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">Archive summary</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="font-serif text-4xl text-secondary">4</p>
                  <p className="mt-2 text-sm leading-6 text-secondary/62">Completed apartment communities</p>
                </div>
                <div>
                  <p className="font-serif text-4xl text-secondary">20</p>
                  <p className="mt-2 text-sm leading-6 text-secondary/62">Rendered brochure pages placed in sections</p>
                </div>
                <div>
                  <p className="font-serif text-4xl text-secondary">100%</p>
                  <p className="mt-2 text-sm leading-6 text-secondary/62">Brochure-backed imagery on this completed view</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14 space-y-14">
            {completedProjects.map((project, projectIndex) => (
              <motion.article
                key={project.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.12 }}
                transition={{ duration: 0.65, delay: projectIndex * 0.04 }}
                className={archiveCardClass}
              >
                <div className="grid gap-0 xl:grid-cols-[minmax(360px,0.9fr)_minmax(0,1.1fr)]">
                  <div className={archiveMediaPanelClass}>
                    <div className={archiveBadgeClass}>
                      <BookImage size={14} />
                      {project.coverLabel}
                    </div>

                    <div className={archiveImageFrameClass}>
                      <img
                        src={project.coverImage}
                        alt={`${project.title} brochure cover`}
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  <div className="p-6 md:p-8">
                    <div className={archiveMetaRowClass}>
                      <span className={archiveMetaBadgeClass}>
                        <CheckCircle2 size={14} />
                        Completed Project
                      </span>
                      <span className={archiveMetaBadgeClass}>
                        <MapPin size={14} />
                        {project.location}
                      </span>
                    </div>

                    <h2 className={archiveTitleClass}>{project.title}</h2>
                    <p className={archiveSubheadClass}>{project.unitMix}</p>
                    <p className={archiveCopyClass}>{project.summary}</p>

                    <div className="mt-8 flex flex-wrap gap-3">
                      {project.highlights.map((item) => (
                        <span key={item} className={archiveHighlightClass}>
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={archiveSectionsWrapClass}>
                  <div className="space-y-8">
                    {project.sections.map((section) => (
                      <section key={`${project.title}-${section.title}`} className={archiveSectionCardClass}>
                        <div className="md:flex md:items-start md:justify-between md:gap-8">
                          <div className="max-w-3xl">
                            <p className={archiveSectionLabelClass}>{project.title}</p>
                            <h3 className={archiveSectionTitleClass}>{section.title}</h3>
                            <p className={archiveSectionCopyClass}>{section.description}</p>
                          </div>
                        </div>

                        <div className={`mt-6 grid gap-4 ${getBrochureGridClass(section.images.length)}`}>
                          {section.images.map((image, imageIndex) => (
                            <div key={`${section.title}-${imageIndex}`} className={archiveSectionImageClass}>
                              <img
                                src={image}
                                alt={`${project.title} ${section.title} brochure page ${imageIndex + 1}`}
                                className="h-full w-full object-contain"
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const featuredProject = liveProjects[0];
  const supportingProjects = liveProjects.slice(1);
  const pageIntro = {
    kicker: 'Selected work',
    title: 'Projects shaped to feel precise, generous, and unmistakably lived in.',
    detail:
      'Deeper setbacks, quieter palettes, shaded arrivals, and a stronger sense of procession from street to front door.',
  };

  return (
    <section className="relative overflow-hidden bg-primary pb-28 pt-36 text-secondary md:pb-32 md:pt-44">
      <div className="paper-grid absolute inset-0 opacity-[0.08]" />
      <div className="floating-orb absolute right-[-10rem] top-16 h-80 w-80 rounded-full bg-accent/15" />

      <div className="section-shell relative">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,0.88fr)_minmax(320px,0.72fr)] lg:items-end">
          <div>
            <span className="editorial-kicker text-gold-light before:bg-gold-light">{pageIntro.kicker}</span>
            <h1 className="mt-8 max-w-4xl text-balance text-4xl leading-tight text-secondary sm:text-5xl md:text-6xl">
              {pageIntro.title}
            </h1>
          </div>
          <div className="rounded-[1.75rem] border border-white/10 bg-white/5 p-6 backdrop-blur">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">What you will notice</p>
            <p className="mt-3 text-base leading-8 text-secondary/70">{pageIntro.detail}</p>
          </div>
        </div>

        <motion.article
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mt-14 overflow-hidden rounded-[2.25rem] border border-white/10 bg-white/5 lg:grid lg:grid-cols-[1.1fr_0.9fr]"
        >
          <div className="relative min-h-[420px]">
            <img
              src={featuredProject.image}
              alt={featuredProject.title}
              className="absolute inset-0 h-full w-full object-cover"
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/5 to-primary/70" />
          </div>

          <div className="flex flex-col justify-between p-8 md:p-10">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">Featured project</p>
              <h2 className="mt-4 text-4xl text-secondary">{featuredProject.title}</h2>
              <p className="mt-5 max-w-2xl text-base leading-8 text-secondary/72">{featuredProject.summary}</p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-5">
                  <div className="flex items-center gap-3 text-sm text-secondary/65">
                    <MapPin size={16} className="text-gold-light" />
                    {featuredProject.location}
                  </div>
                </div>
                <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-5">
                  <div className="flex items-center gap-3 text-sm text-secondary/65">
                    <CalendarDays size={16} className="text-gold-light" />
                    {featuredProject.year}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">Program</p>
                <p className="mt-2 font-serif text-2xl text-secondary">{featuredProject.type}</p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 rounded-full bg-accent px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:bg-gold-light hover:text-primary"
              >
                Request Details
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </motion.article>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {supportingProjects.map((project, index) => (
            <motion.article
              key={project.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: index * 0.06 }}
              className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/5"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
              </div>

              <div className="p-6">
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-gold-light/85">
                    {project.type}
                  </span>
                  <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-secondary/70">
                    {project.year}
                  </span>
                </div>
                <h3 className="mt-5 text-3xl text-secondary">{project.title}</h3>
                <div className="mt-3 flex items-center gap-3 text-sm text-secondary/58">
                  <MapPin size={15} className="text-gold-light" />
                  {project.location}
                </div>
                <p className="mt-5 leading-7 text-secondary/68">{project.summary}</p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 rounded-[2rem] border border-white/10 bg-white/5 p-8 text-center backdrop-blur md:p-10">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">Need the completed archive?</p>
          <p className="mx-auto mt-4 max-w-2xl text-2xl font-serif text-secondary md:text-3xl">
            Use the Projects submenu and open Project Completed to browse brochure sections for delivered communities.
          </p>
          <Link
            to="/projects?category=completed"
            className="mt-7 inline-flex items-center gap-3 rounded-full bg-accent px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:bg-gold-light hover:text-primary"
          >
            Open Completed Projects
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
